import _ from 'lodash';
import { action, computed } from 'mobx';
import { Trans } from 'react-i18next';
import { resourceApi } from 'src/services/apiServices';
import { Resource } from 'src/services/customApi/ResourceApiFactory';
import { AsyncOperationWithStatus } from 'src/utils/mobx/AsyncOperationWithStatus';
import { RequestHelper } from 'src/utils/RequestHelper';

export class ResourceStore {
    isLoadTriggered = false;
    resourceLoader: AsyncOperationWithStatus<() => Promise<Resource[]>>;

    constructor(
        loadResourceKey: string,
        private optionsSorter?: OptionsSorterFn,
    ) {
        this.resourceLoader = new AsyncOperationWithStatus(() => {
            return RequestHelper.unwrapFromAxiosPromise<Resource[]>(
                resourceApi.getResource({
                    resourcePath: loadResourceKey,
                }),
            );
        });
    }

    resourceItemToOption(resource: Resource): OptionType<Resource> {
        return {
            label: <Trans i18nKey={resource.label} />,
            value: resource.value,
            item: resource,
        };
    }

    @action
    async load() {
        if (!this.isLoadTriggered) {
            this.isLoadTriggered = true;
            await this.resourceLoader.call();
        }
    }

    @computed
    get resourceList() {
        return this.resourceLoader.data || [];
    }

    @computed
    get itemsMap(): Record<string, Resource> {
        return _.mapKeys(this.resourceList, 'value');
    }

    @computed
    get options(): OptionType<Resource>[] {
        const options =
            this.resourceList?.map((item) => this.resourceItemToOption(item)) ??
            [];

        return this.optionsSorter ? options.sort(this.optionsSorter) : options;
    }
}

export type OptionType<Item> = {
    value: any;
    label: React.ReactNode;
    item?: Item;
};

export type OptionsSorterFn = (
    a: OptionType<Resource>,
    b: OptionType<Resource>,
) => number;
